import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { trackPageview } from "@/lib/track";

// Rebuild placeholder (authenticated landing)
import ComingSoon from "@/pages/ComingSoon";

// Public brand home
import Home from "@/pages/Home";

// Public newsletter landing (shell)
import SundayReset from "@/pages/SundayReset";

// Public Life Audit (free assessment)
import LifeAudit from "@/pages/LifeAudit";

// Auth + onboarding (preserved, still working)
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import GoogleCallback from "@/pages/GoogleCallback";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";

// Admin surfaces (preserved + access-gated)
import Admin from "@/pages/Admin";
import Superadmin from "@/pages/Superadmin";

/*
  ─────────────────────────────────────────────────────────────────────────────
  "START OVER" — BLANK SLATE (rebuild in progress)
  ─────────────────────────────────────────────────────────────────────────────
  The previous marketing site AND the full Life OS app are intentionally
  UNROUTED for the rebuild. All of their source files remain in the codebase
  and can be restored at any time by re-adding their imports + <Route> entries.

  Parked (still on disk, not routed):
    • Marketing:  src/marketing/*  (Home, Framework, Philosophy, WhoItsFor,
                  HowItWorks, Pricing, Journal)
    • Life OS:    src/pages/Dashboard, Annual, Quarterly, Monthly, Weekly,
                  Daily, Departments, Faith, Health, Exec, Wealth,
                  Relationships, Analytics, Settings, Profile

  Kept live:
    • "/"            → ComingSoon placeholder
    • Auth routes    → login / signup / verify / reset / google
    • "/dashboard"   → authenticated landing (ComingSoon) so auth flows resolve
    • Admin surfaces → "/admin" and "/console-7h3x9q" (gated to admins/master)
  ─────────────────────────────────────────────────────────────────────────────
*/

const Loader = () => (
  <div className="id-surface flex min-h-screen items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--id-mist)] border-t-[var(--id-gold)]" />
      <p className="id-serif text-lg text-[var(--id-charcoal)]">Intrinsic Daily</p>
    </div>
  </div>
);

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Gate admin surfaces to the master account or accounts flagged as admin.
// Everyone else is redirected to the placeholder. All admin code stays intact.
const RequireAdmin = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!(user.is_admin || user.is_master)) return <Navigate to="/" replace />;
  return children;
};

// Minimal padded shell for the preserved admin surfaces (no legacy product nav).
const AdminShell = ({ children }) => (
  <div className="min-h-screen w-full bg-background">
    <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8 py-8">{children}</div>
  </div>
);

const RoutedApp = () => {
  const location = useLocation();
  // Record a first-party pageview on every route change (public site analytics).
  React.useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname, location.search]);
  // CRITICAL: process Emergent OAuth return (session_id in URL hash) BEFORE any
  // route runs, to avoid redirect races.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public brand home */}
      <Route path="/" element={<Home />} />

      {/* Public newsletter landing */}
      <Route path="/sunday-reset" element={<SundayReset />} />

      {/* Public Life Audit */}
      <Route path="/life-audit" element={<LifeAudit />} />

      {/* Auth (preserved, working) */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />

      {/* Authenticated landing during the rebuild */}
      <Route path="/dashboard" element={<RequireAuth><ComingSoon /></RequireAuth>} />

      {/* Admin surfaces (preserved + gated) */}
      <Route path="/admin" element={<RequireAdmin><AdminShell><Admin /></AdminShell></RequireAdmin>} />
      <Route path="/console-7h3x9q" element={<RequireAdmin><AdminShell><Superadmin /></AdminShell></RequireAdmin>} />
      <Route path="/hq" element={<Navigate to="/console-7h3x9q" replace />} />

      {/* Everything else → placeholder */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RoutedApp />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
